import * as rd from '@devexperts/remote-data-ts'
import { BookingPeriod } from '@spiaggeit/spit-datepicker'
import { useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { INSERT_PERIOD_PATH } from '@/app/router/paths'
import { AppMode } from '@/models/app'
import { appSlice, isKioskSelector } from '@/store/appSlice'
import { setupByUrl } from '@/store/commonActions'
import { insertPeriodSlice } from '@/store/insertPeriodSlice'

import { licenseSlice } from '@/store/licenseSlice'
import { getBookingFlowFromSearchParams } from '@/utils/bookingFlow'
import { getIsPool, getIsPoolBeachType, setupPool } from '@/utils/poolUtils'
import { isPeriodMatchingSeasonalRange } from '@/utils/seasonalPeriodRange'
import { bookingAvailabilitySlice } from '../store/bookingAvailabilitySlice'

import { useAppDispatch, useAppSelector } from './store'
import { useCurrentPath } from './useCurrentPath'

export function useApp() {
  const dispatch = useAppDispatch()
  const routeParams = useParams()
  const licenseRemoteData = useAppSelector(
    licenseSlice.selectors.licenseRemoteData
  )
  const license = useAppSelector(licenseSlice.selectors.license)
  const period = useAppSelector(insertPeriodSlice.selectors.period)
  const bookingPeriod = useAppSelector(
    insertPeriodSlice.selectors.bookingPeriod
  )
  const seasonalStart = useAppSelector(licenseSlice.selectors.seasonalStart)
  const seasonalEnd = useAppSelector(licenseSlice.selectors.seasonalEnd)
  const isKiosk = useAppSelector(isKioskSelector)
  const appMode = useAppSelector(appSlice.selectors.mode)
  const isPool = useAppSelector(appSlice.selectors.isPool)
  const { currentPath } = useCurrentPath()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    dispatch(setupByUrl(window.location.href))
  }, [])

  useEffect(() => {
    const shouldUsePoolMode =
      getIsPool() ||
      (appMode === AppMode.WIDGET && getIsPoolBeachType(license?.beach.type))

    if (!shouldUsePoolMode) {
      return
    }

    if (!isPool) {
      dispatch(appSlice.actions.setIsPool(true))
    }

    setupPool()
  }, [appMode, dispatch, isPool, license?.beach.type])

  useEffect(() => {
    if (
      currentPath === INSERT_PERIOD_PATH ||
      !rd.isSuccess(licenseRemoteData) ||
      !license?.bookingSeasonal ||
      isKiosk ||
      bookingPeriod !== BookingPeriod.ALL_DAY ||
      !period
    ) {
      return
    }

    if (isPeriodMatchingSeasonalRange(period, seasonalStart, seasonalEnd)) {
      dispatch(
        insertPeriodSlice.actions.setBookingPeriod(BookingPeriod.ALL_SEASON)
      )
    }
  }, [
    bookingPeriod,
    currentPath,
    dispatch,
    isKiosk,
    licenseRemoteData,
    license?.bookingSeasonal,
    period,
    seasonalEnd,
    seasonalStart,
  ])

  useEffect(() => {
    if (routeParams.license && rd.isInitial(licenseRemoteData)) {
      dispatch(licenseSlice.actions.loadLicense(routeParams.license))
      dispatch(licenseSlice.actions.loadMainTimeSlots(routeParams.license))
    }
  }, [licenseRemoteData, routeParams])

  useEffect(() => {
    const bookingFlow = getBookingFlowFromSearchParams(searchParams)
    dispatch(
      bookingAvailabilitySlice.actions.load({
        bookingFlow,
      })
    )
  }, [])
}
